import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, IconButton, Dialog, Slide, Typography
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { fetchData, deleteData } from "../../BaseUrl";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import MandateLeaveForm from "./MandateLeaveForm";
import Toast from "../../DynamicComponent/Toast";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const fallbackRows = [
  { id: null, empId: "EMP001", teacherName: "Mrs. Lakshmi", leaveType: "Mandate Leave", substitute: "Mr. Rajan", reason: "Medical", totalDays: 2, status: "Approved" },
  { id: null, empId: "EMP002", teacherName: "Mr. Karthik", leaveType: "Casual Leave", substitute: "Mrs. Priya", reason: "Personal", totalDays: 1, status: "Pending" },
  { id: null, empId: "EMP003", teacherName: "Mrs. Anitha", leaveType: "Sick Leave", substitute: "Mr. Suresh", reason: "Fever", totalDays: 3, status: "Approved" },
  { id: null, empId: "EMP004", teacherName: "Mr. Venkat", leaveType: "Mandate Leave", substitute: "Mrs. Kavitha", reason: "Family", totalDays: 2, status: "Rejected" },
  { id: null, empId: "EMP005", teacherName: "Mrs. Deepa", leaveType: "Casual Leave", substitute: "Mr. Mohan", reason: "Personal", totalDays: 1, status: "Approved" },
];

const statusLabel = (status) => {
  if (status === "Rejected") return "UnApproved";
  return status;
};

const statusStyle = (status) => {
  if (status === "Approved") return { backgroundColor: "#e8f5e9", color: "#4caf50" };
  if (status === "Rejected") return { backgroundColor: "#ffebee", color: "#f44336" };
  return { backgroundColor: "#fff3e0", color: "#f57c00" };
};

const actionBtnStyle = {
  border: "1px solid #D5D7DA",
  borderRadius: "8px",
  color: "#8F95B2",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 14px",
};

export default function LeaveManagement() {
  const [rawData, setRawData] = useState([]);
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const handleDeleteRequest = useCallback((row) => setDeleteRow(row), []);

  const handleDeleteConfirm = async () => {
    if (!deleteRow?.id) { setDeleteRow(null); return; }
    try {
      await deleteData(`teacher/leave/${deleteRow.id}`);
      setToast({ open: true, severity: "success", message: "Leave record deleted" });
      setDeleteRow(null);
      loadData();
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Delete failed" });
      setDeleteRow(null);
    }
  };

  const buildRows = useCallback((data, onDelete) => {
    return data.map((r) => ({
      empId: r.empId || r.employeeId,
      teacherName: r.teacherName || r.name,
      leaveType: r.leaveType,
      substitute: r.substitute,
      reason: r.reason,
      totalDays: r.totalDays,
      downloadReport: (
        <IconButton
          size="small"
          sx={{ color: "#244C77" }}
          onClick={() => r.reportUrl && window.open(r.reportUrl, "_blank")}
          disabled={!r.reportUrl}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      ),
      statusChip: (
        <Chip label={statusLabel(r.status)} size="small" sx={{ ...statusStyle(r.status), fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
      ),
      deleteBtn: (
        <IconButton size="small" sx={{ color: "#f44336" }} onClick={() => onDelete(r)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = useCallback(async () => {
    try {
      const response = await fetchData("teacher/leave/list");
      const data = response?.data?.leaves && Array.isArray(response.data.leaves)
        ? response.data.leaves
        : (response?.data && Array.isArray(response.data) ? response.data : fallbackRows);
      setRawData(data);
      setRows(buildRows(data, handleDeleteRequest));
    } catch (_) {
      setRawData(fallbackRows);
      setRows(buildRows(fallbackRows, handleDeleteRequest));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildRows, handleDeleteRequest]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (rawData.length >= 0) setRows(buildRows(rawData, handleDeleteRequest));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData]);

  const columns = [
    { label: "Employee ID", field: "empId" },
    { label: "Teacher's Name", field: "teacherName" },
    { label: "Leave Type", field: "leaveType" },
    { label: "Substitute", field: "substitute" },
    { label: "Reason", field: "reason" },
    { label: "Total Days", field: "totalDays" },
    { label: "Download Report", field: "downloadReport" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "deleteBtn" },
  ];

  return (
    <Box sx={{ padding: "1rem", textAlign: "left" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <p style={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Leave Management
      </p>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <h2 style={{ margin: 0 }}>Leave Management</h2>
          <Chip label={`${rawData.length} Records`} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button startIcon={<DeleteOutlineIcon />} sx={actionBtnStyle}>Delete</Button>
          <Button startIcon={<FilterListIcon />} sx={actionBtnStyle}>Filters</Button>
          <Button startIcon={<FileDownloadIcon />} sx={actionBtnStyle}>Export</Button>
          <Button startIcon={<AddIcon />} onClick={() => setFormOpen(true)} sx={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none", borderRadius: "8px", padding: "8px 20px", fontSize: "13px" }}>
            + Apply Leave Form
          </Button>
        </Box>
      </Box>
      <DynamicTable columns={columns} rows={rows} />

      {/* Apply Leave Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} TransitionComponent={Transition} fullWidth maxWidth="sm">
        <MandateLeaveForm handleClose={() => { setFormOpen(false); loadData(); }} />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Delete Leave Record</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>
            Are you sure you want to delete this leave record? This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteRow(null)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#d32f2f" } }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
